/**
 * reports/ai/aiAssistant.js
 *
 * Keyword-driven prompt parser + heuristic insight generator. Lives here so
 * the LLM swap (POST /reports/ai/generate, /reports/:id/ai/insights) only
 * touches this file.
 */
import { getObject } from '../../analytics-shared/fixtures/reportObjects';
import { formatAggValue, displayLabel } from '../../analytics-shared/engine/reportEngine';
import { createEmptyFilter, uid } from '../api/reportsApi';

const OBJECT_KEYWORDS = [
  ['deals', ['deal', 'opportunit', 'pipeline', 'won deal', 'sales']],
  ['leads', ['lead']],
  ['contacts', ['contact', 'people']],
  ['companies', ['company', 'companies', 'account']],
  ['tickets', ['ticket', 'support', 'case', 'csat']],
  ['campaigns', ['campaign', 'marketing', 'roi']],
  ['invoices', ['invoice', 'billing', 'payment']],
  ['tasks', ['task', 'to-do', 'todo']],
  ['calls', ['call']],
  ['meetings', ['meeting', 'demo']],
];

function detectObject(text) {
  for (const [key, kws] of OBJECT_KEYWORDS) {
    if (kws.some((k) => text.includes(k))) return key;
  }
  return 'deals';
}

function fieldByHint(object, hint) {
  return object.fields.find((f) => f.label.toLowerCase().includes(hint) || f.key.includes(hint));
}

export function generateReportFromPrompt(prompt) {
  const text = (prompt || '').toLowerCase();
  const objectKey = detectObject(text);
  const object = getObject(objectKey);

  const groupBy = [];
  const byMatches = [...text.matchAll(/by ([a-z ]+?)(?:\s(?:and|in|for|with|,)|$)/g)];
  byMatches.forEach((m) => {
    const hint = m[1].trim().split(' ')[0];
    const field = fieldByHint(object, hint);
    if (field && !groupBy.includes(field.key)) groupBy.push(field.key);
  });
  const wantsTime = /month|monthly|trend|over time|timeline|weekly/.test(text);
  const dateField = object.fields.find((f) => f.type === 'date');
  if (wantsTime && dateField && !groupBy.includes(dateField.key)) groupBy.unshift(dateField.key);
  if (!groupBy.length) {
    const firstSelect = object.fields.find((f) => f.type === 'select');
    if (firstSelect) groupBy.push(firstSelect.key);
  }

  const numField = object.fields.find((f) => /amount|revenue|value|budget|roi/.test(f.key))
    || object.fields.find((f) => ['currency', 'number', 'percent'].includes(f.type));
  const summaries = {};
  let agg = 'SUM';
  if (/average|avg|mean/.test(text)) agg = 'AVG';
  if (/count|how many|number of/.test(text) && !numField) agg = 'COUNT';
  if (numField) summaries[numField.key] = agg;

  const filters = createEmptyFilter();
  if (objectKey === 'deals' && /\bwon\b/.test(text)) {
    filters.children.push({ id: uid('c'), type: 'condition', field: 'stage', operator: 'equals', value: 'Closed Won' });
  }
  if (objectKey === 'tickets' && /open|unresolved/.test(text)) {
    filters.children.push({ id: uid('c'), type: 'condition', field: 'status', operator: 'is_one_of', value: ['New', 'Open', 'Pending'] });
  }

  const columns = [...groupBy];
  if (numField && !columns.includes(numField.key)) columns.push(numField.key);
  object.fields.slice(0, 6).forEach((f) => {
    if (columns.length < 5 && !columns.includes(f.key)) columns.push(f.key);
  });

  let chartType = 'bar';
  if (/pie/.test(text)) chartType = 'pie';
  else if (/donut/.test(text)) chartType = 'donut';
  else if (/funnel/.test(text)) chartType = 'funnel';
  else if (/line|trend|over time/.test(text)) chartType = 'line';
  else if (wantsTime) chartType = 'line';
  else if (objectKey === 'deals' && groupBy.includes('stage')) chartType = 'funnel';

  const charts = [{
    id: uid('ch'),
    type: chartType,
    groupField: groupBy[groupBy.length - 1] || (object.fields[0] && object.fields[0].key),
    valueField: numField ? numField.key : '',
    agg: numField ? agg : 'COUNT',
    title: 'AI Generated Chart',
  }];

  const name = prompt && prompt.trim()
    ? prompt.trim().replace(/^(show|give me|create|build|get)\s+/i, '').replace(/^\w/, (c) => c.toUpperCase())
    : `${object.label} Report`;

  return { object: objectKey, name: name.slice(0, 60), columns, groupBy, summaries, charts, filters };
}

export const AI_PROMPT_SUGGESTIONS = [
  'Show monthly won deals by owner',
  'Pipeline value by stage as a funnel',
  'Lead count by source as a pie chart',
  'Average ROI by campaign channel',
  'Open tickets by priority',
];

export function getInsights(result, config, fieldMap) {
  const insights = [];
  if (!result || !result.records || !result.records.length) {
    return [{ id: uid('i'), type: 'summary', title: 'No data', text: 'This report returned no records. Try loosening the filters.' }];
  }
  const records = result.records;
  const measureKey = Object.keys(config.summaries || {})[0];
  const measureAgg = measureKey ? config.summaries[measureKey] : 'COUNT';
  const measureField = measureKey ? fieldMap[measureKey] : null;
  const measureLabel = measureField ? (displayLabel(measureField) || measureField.label) : 'Records';

  // Total of the measure field. After wedge 5 the engine returns
  // pre-computed grandTotals on every RunResult, so the AI insights
  // don't need to re-aggregate client-side — they just read the value
  // the server already computed for that summary.
  const measureTotal = measureKey
    ? (result.grandTotals || {})[measureKey] ?? records.length
    : records.length;
  insights.push({
    id: uid('i'), type: 'summary', title: 'Dataset overview',
    text: `Report covers ${records.length.toLocaleString()} records. Total ${measureLabel}: ${formatAggValue(measureTotal, measureKey, measureAgg, fieldMap)}.`,
  });

  if (result.grouped && result.tree && result.tree.length) {
    const total = result.tree.reduce((s, n) => s + (measureKey ? n.summaries[measureKey] || 0 : n.count), 0) || 1;
    const valueOf = (n) => (measureKey ? n.summaries[measureKey] || 0 : n.count);
    const sorted = [...result.tree].sort((a, b) => valueOf(b) - valueOf(a));
    const top = sorted[0];
    const bottom = sorted[sorted.length - 1];
    const groupFieldLabel = displayLabel(fieldMap[config.groupBy[0]]) || 'group';

    insights.push({
      id: uid('i'), type: 'trend', title: 'Top performer',
      text: `"${top.value}" leads by ${groupFieldLabel.toLowerCase()} with ${Math.round((valueOf(top) / total) * 100)}% of the total (${formatAggValue(valueOf(top), measureKey, measureAgg, fieldMap)}).`,
    });

    if (sorted.length > 1 && valueOf(bottom) < (total / sorted.length) * 0.4) {
      insights.push({
        id: uid('i'), type: 'anomaly', title: 'Underperforming segment',
        text: `"${bottom.value}" is well below average — only ${formatAggValue(valueOf(bottom), measureKey, measureAgg, fieldMap)}. Worth investigating.`,
      });
    }

    if (config.groupBy.includes('stage')) {
      const order = ['Qualification', 'Needs Analysis', 'Proposal', 'Negotiation', 'Closed Won'];
      const byStage = {};
      result.tree.forEach((n) => { byStage[n.value] = valueOf(n); });
      for (let i = 1; i < order.length; i += 1) {
        const prev = byStage[order[i - 1]]; const curr = byStage[order[i]];
        if (prev && curr && curr < prev * 0.6) {
          const drop = Math.round((1 - curr / prev) * 100);
          insights.push({
            id: uid('i'), type: 'anomaly', title: 'Conversion drop-off',
            text: `${measureLabel} fell ${drop}% between "${order[i - 1]}" and "${order[i]}" — the biggest leak in the funnel.`,
          });
          break;
        }
      }
    }

    insights.push({
      id: uid('i'), type: 'suggestion', title: 'Recommended chart',
      text: `With ${result.tree.length} groups, a ${result.tree.length > 6 ? 'horizontal bar' : 'pie'} chart will read best for this breakdown.`,
    });
  } else {
    insights.push({
      id: uid('i'), type: 'suggestion', title: 'Add a grouping',
      text: 'This report is ungrouped. Drag a field into Group By (e.g. Stage or Owner) to unlock subtotals and charts.',
    });
  }

  return insights;
}
